import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { TabContent, TabPane } from "reactstrap";
import { withRouter, useParams, useHistory } from "react-router-dom";

import {
  TABS,
  SPACE,
  ROUTES,
  ALL_EVENT,
  EMPTY_STRING,
  stepTitleKeys,
  localStorageKeys,
  eventBookingParams,
  eventBookingStatus,
  eventBookingConstants,
} from "../../../constants";
import { ReactComponent as CartIcon } from "../../../assets/icons/cart.svg";
import { ReactComponent as AddRecapIcon } from "../../../assets/icons/loop.svg";
import { ReactComponent as CheckoutIcon } from "../../../assets/icons/checkout.svg";
import { ReactComponent as EventSetupIcon } from "../../../assets/icons/event-setup.svg";
import { ReactComponent as EventDetailsIcon } from "../../../assets/icons/chart-detail.svg";
import { ReactComponent as ShutterbResultsIcon } from "../../../assets/icons/search-image.svg";

import {
  filterByStatus,
  normalizeEventDates,
  setStatusEventBooking,
  transformReelsInEditEvent,
  isMatchingDraftEventGroup,
} from "../../../shared";
import {
  eventBookingActions,
  configurationsActions,
} from "../../../redux/actions";
import {
  NavigateToPath,
  setItemToStorage,
  getItemFromStorage,
} from "../../../system";
import AddRecapz from "./Tabs/AddRecapz";
import CartSummary from "./Tabs/CartSummary";
import EventDetails from "./Tabs/EventDetails";
import EventSetupForm from "./Tabs/EventSetupForm";
import ShutterBSelectionForm from "./Tabs/ShutterBSelectionForm";
import HorizontalStepper from "../../../shared/components/Stepper/HorizontalStepper";

const { TABS_ID, PARTNER_CODE, AFFILIATE_CODE } = eventBookingConstants;

const iconMap = {
  1: <EventSetupIcon />,
  2: <ShutterbResultsIcon />,
  3: <EventDetailsIcon />,
  4: <AddRecapIcon />,
  5: <CartIcon />,
  6: <CheckoutIcon />,
};

const EventBookingForm = (props) => {
  const history = useHistory();
  const { t } = useTranslation("common");
  const { isBookingStatus, eventGroupId, eventId } = useParams();

  const isDraftEvent = isBookingStatus === eventBookingParams.DRAFT_EVENT;
  const isEditEvent = isBookingStatus === eventBookingParams.EDIT_EVENT;

  const affiliateCode = localStorage.getItem(AFFILIATE_CODE);
  const partnerCodeUrlParam = new URLSearchParams(history.location.search).get(
    PARTNER_CODE
  );

  const editEventRef = useRef(true);
  const [avFormState, setAvFormState] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    const savedStep = getItemFromStorage(localStorageKeys.ACTIVE_TAB);
    const savedStepValue =
      savedStep !== null ? Number(savedStep) : TABS_ID.EVENT_SETUP;
    if (
      isMatchingDraftEventGroup(eventGroupId, props.draftEvents?.eventGroupId)
    ) {
      return savedStepValue;
    }

    if (isDraftEvent) {
      return TABS_ID.SELECT_SHUTTERB;
    } else if (isEditEvent) {
      return TABS_ID.EVENT_SETUP;
    }

    return savedStepValue;
  });

  useEffect(() => {
    if (eventGroupId && affiliateCode) {
      history.push(`?partnerCode=${affiliateCode}`);
    } else if (partnerCodeUrlParam) {
      localStorage.setItem("affiliateCode", partnerCodeUrlParam);

      if (affiliateCode !== partnerCodeUrlParam) {
        window.location.href = process.env.REACT_APP_MARKETING_SITE_BASE_URL;
      } else {
        history.push(
          `${process.env.REACT_APP_MARKETING_EVENT_BOOKING_URL}${partnerCodeUrlParam}`
        );
      }
    } else if (affiliateCode) {
      // Use history.push() for redirection
      history.push(
        `${process.env.REACT_APP_MARKETING_EVENT_BOOKING_URL}${affiliateCode}`
      );
    }
    // eslint-disable-next-line
  }, [history.location.search]);

  useEffect(() => {
    props.getRecapTypes();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setItemToStorage(localStorageKeys.ACTIVE_TAB, activeTab);
    props.setPreviousActiveTab(t(TABS[activeTab - 1]) || EMPTY_STRING);

    if (activeTab === TABS_ID.EVENT_SETUP) {
      props.getPostalCodes();
      if (isEditEvent && editEventRef.current) {
        props.resetEventBooking().then(() => {
          props.getEditEvents(eventGroupId, eventId, onGetEditEventSuccess);
          editEventRef.current = false;
        });
        editEventRef.current = false;
      }
    }

    // eslint-disable-next-line
  }, [activeTab]);

  // This function is triggered when the "get edit events" call is executed,
  // and it handles either a response or null.
  const onGetEditEventSuccess = async (response) => {
    if (response) {
      let updatedFormState = normalizeEventDates(response.events);

      // Set status "EDITED" for events
      // Set status "EDITED" and oldHourlyRate for orderItems inside each event
      updatedFormState = setStatusEventBooking(
        updatedFormState.map((event) => {
          const updatedOrderItems = setStatusEventBooking(
            event?.orderItems?.map((orderItem) => ({
              ...orderItem,
              hourlyRate: orderItem?.sbUser?.hourlyRate ?? 0,
              oldHourlyRate: orderItem?.sbUser?.oldHourlyRate ?? 0,
            })),
            eventBookingStatus.EDITED
          );

          return {
            ...event,
            orderItems: updatedOrderItems,
            oldDuration: event.duration ?? 0,
          };
        }),
        eventBookingStatus.EDITED
      );
      props.setFormState(updatedFormState);
      props.setEditEvents({ ...response, events: updatedFormState });
      props.setIsEditEvent(isEditEvent);

      // Update the structure of recaps to show in the Add Recap page in event booking flow
      const updatedRecapz = transformReelsInEditEvent(
        response.reels,
        ALL_EVENT.id
      );
      props.setAddRecapz(updatedRecapz);
      props.setTotalEvents(response.totalOrderEvents);

      // backup recaps in case of Edit Event to restore the cancel recaps while editing event
      props.setAddRecapzBackup(updatedRecapz);

      if (response.stripeOrderPayment) {
        let miscAmounts = {
          // mutiply by 100 because later use method (calculatePercentage) in cart summary table page
          // which required the actual value of percentage
          tax: response.taxPercentage * 100,
          serviceFee: response.serviceFeePercentage * 100,
        };
        props.setMiscAmountsOld(miscAmounts);
      }
    } else {
      // If the response is null then update the path to "book new event"
      NavigateToPath(history, ROUTES.EVENT_BOOKING);
    }
  };

  const handleNext = () => {
    setActiveTab((activeTab) => activeTab + 1);
  };

  const handleStepClick = (index) => {
    if (index <= activeTab) {
      setActiveTab(index);
      props.setIsProfileViewShow(false);
    }
  };

  const handlePrevious = () => {
    setActiveTab((activeTab) => activeTab - 1);
  };

  const resetAvFormKey = () => {
    setAvFormState((prev) => prev + 1);
  };

  return (
    <div className="event-booking-container position-relative my-5">
      <div className="heading-wrapper text-center mb-5">
        <h2 className="heading-title mb-2">
          {activeTab === TABS_ID.EVENT_DETAIL 
          ? t(stepTitleKeys[activeTab])
            : activeTab === TABS_ID.EVENT_SETUP ||
              activeTab === TABS_ID.SELECT_SHUTTERB
            ? t(stepTitleKeys[activeTab])
            : t(stepTitleKeys[activeTab]) +
              SPACE +
              (props.eventName || EMPTY_STRING)}
        </h2>
        <hr className="heading-hr" />
      </div>

      <HorizontalStepper
        activeStep={activeTab}
        steps={TABS}
        onStepClick={handleStepClick}
        iconsMap={iconMap}
        disableInActiveTabs={!props.isSBTipExtendCart}
        isDraftEvent={props.isDraftEvent}
      />

      <div>
        <TabContent activeTab={activeTab} className="tabs_content">
          <TabPane tabId={TABS_ID.EVENT_SETUP}>
            <EventSetupForm onNext={handleNext} avFormState={avFormState} />
          </TabPane>

          <TabPane tabId={TABS_ID.SELECT_SHUTTERB}>
            <ShutterBSelectionForm
              onNext={handleNext}
              onPrevious={handlePrevious}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              resetAvFormKey={resetAvFormKey}
              affiliateCode={
                partnerCodeUrlParam !== null
                  ? partnerCodeUrlParam
                  : affiliateCode
              }
              formState={filterByStatus(props.formState ?? [], [
                eventBookingStatus.CANCEL,
              ])}
            />
          </TabPane>

          <TabPane tabId={TABS_ID.EVENT_DETAIL}>
            <EventDetails
              onNext={handleNext}
              onPrevious={handlePrevious}
              activeTab={activeTab}
            />
          </TabPane>
          <TabPane tabId={TABS_ID.ADD_REEL}>
            <AddRecapz
              onNext={handleNext}
              onPrevious={handlePrevious}
              activeTab={activeTab}
            />
          </TabPane>
          <TabPane tabId={TABS_ID.CART} className="cart-summary-tab">
            <CartSummary
              onNext={handleNext}
              onPrevious={handlePrevious}
              activeTab={activeTab}
              affiliateCode={
                partnerCodeUrlParam !== null
                  ? partnerCodeUrlParam
                  : affiliateCode
              }
            />
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  eventName: state.eventBooking.eventName,
  formState: state.eventBooking.formState,
  editEvents: state.eventBooking.editEvents,
  draftEvents: state.eventBooking.draftEvents,
  isDraftEvent: state.eventBooking.isDraftEvent,
  isSBTipExtendCart: state.eventBooking.isSBTipExtendCart,
  isProfileViewShow: state.eventBooking.isProfileViewShow,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getRecapTypes: () => dispatch(eventBookingActions.getRecapTypes()),
  getPostalCodes: () => dispatch(configurationsActions.getPostalCodes()),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setEditEvents: (data) => dispatch(eventBookingActions.setEditEvents(data)),
  setIsEditEvent: (data) => dispatch(eventBookingActions.setIsEditEvent(data)),
  setTotalEvents: (data) => dispatch(eventBookingActions.setTotalEvents(data)),
  setMiscAmountsOld: (data) =>
    dispatch(eventBookingActions.setMiscAmountsOld(data)),
  setAddRecapzBackup: (data) =>
    dispatch(eventBookingActions.setAddRecapzBackup(data)),
  setIsProfileViewShow: (data) =>
    dispatch(eventBookingActions.setIsProfileViewShow(data)),
  setPreviousActiveTab: (data) =>
    dispatch(eventBookingActions.setPreviousActiveTab(data)),
  getEditEvents: (eventGroupId, eventId, onResponseCallback) =>
    dispatch(
      eventBookingActions.getEditEvents(
        eventGroupId,
        eventId,
        onResponseCallback
      )
    ),
  resetEventBooking: () => dispatch(eventBookingActions.resetEventBooking()),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(EventBookingForm));
